import { queryOptions } from "@tanstack/react-query";

import { parseClipSidecar, type ClipSidecar } from "@shared/clipSidecar";

/** Distinguishes "this link is wrong" from "this bucket is having a bad day". */
export class ClipFetchError extends Error {
  constructor(
    message: string,
    readonly kind: "not_found" | "forbidden" | "network" | "malformed"
  ) {
    super(message);
    this.name = "ClipFetchError";
  }
}

const SIDECAR_TIMEOUT_MS = 10_000;

/**
 * A missing or unreadable sidecar is not fatal — the video may well still play,
 * and older shares predate the sidecar entirely. Callers decide how much of the
 * page to render without one.
 */
const fetchSidecar = async (url: string, signal: AbortSignal): Promise<ClipSidecar> => {
  const timeout = AbortSignal.timeout(SIDECAR_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(url, {
      signal: AbortSignal.any([signal, timeout]),
      // The bucket is public and the link is the only credential; sending
      // cookies would just break the CORS preflight.
      credentials: "omit",
      // Shares are deleted / expire; never reuse a cached 200 after the objects
      // are gone (React Query also keeps this fresh — see query options).
      cache: "no-store"
    });
  } catch (err) {
    if (signal.aborted) throw err;
    throw new ClipFetchError(
      "Could not reach the bucket this clip is stored in.",
      err instanceof DOMException && err.name === "TimeoutError" ? "network" : "network"
    );
  }

  if (response.status === 404) {
    throw new ClipFetchError("This clip no longer exists.", "not_found");
  }
  if (response.status === 401 || response.status === 403) {
    throw new ClipFetchError("This clip is no longer public.", "forbidden");
  }
  if (!response.ok) {
    throw new ClipFetchError(`The bucket returned ${response.status}.`, "network");
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    throw new ClipFetchError("This clip's metadata could not be read.", "malformed");
  }

  const parsed = parseClipSidecar(raw);
  if (!parsed) throw new ClipFetchError("This clip's metadata could not be read.", "malformed");
  return parsed;
};

export const clipSidecarQuery = (sidecarUrl: string) =>
  queryOptions({
    queryKey: ["clip-sidecar", sidecarUrl] as const,
    queryFn: ({ signal }) => fetchSidecar(sidecarUrl, signal),
    // Do not keep a live share's sidecar around — after delete/expiry a revisit
    // must hit the bucket again instead of painting the last successful load.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    retry: (failureCount, error) =>
      error instanceof ClipFetchError && error.kind === "network" ? failureCount < 2 : false
  });
